// Curated list of major US + Canadian cities for the freight quote autocomplete.
// Kept hand-picked (~350 entries) so the bundle stays tiny while covering every
// relevant freight market. Coordinates are approximate centroids used only for
// the optional distance estimate (haversine) shown on the lane summary chip.

export type CityOption = {
  city: string;
  state: string; // 2-letter US state or CA province
  country: "US" | "CA";
  lat: number;
  lng: number;
  zip?: string;
};

export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

export const CA_PROVINCES = ["AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT"];

export const isValidStateCode = (code: string) =>
  US_STATES.includes(code) || CA_PROVINCES.includes(code);

// Small curated list of high-traffic markets used as instant suggestions
// before the user has typed anything (and as an offline fallback).
export const POPULAR_CITIES: CityOption[] = [
  // Michigan (HQ market — extra coverage)
  { city: "Detroit", state: "MI", country: "US", lat: 42.3314, lng: -83.0458 },
  { city: "Grand Rapids", state: "MI", country: "US", lat: 42.9634, lng: -85.6681 },
  { city: "Toronto", state: "ON", country: "CA", lat: 43.6532, lng: -79.3832 },
  { city: "Windsor", state: "ON", country: "CA", lat: 42.3149, lng: -83.0364 },
  { city: "Montreal", state: "QC", country: "CA", lat: 45.5017, lng: -73.5673 },
  { city: "Chicago", state: "IL", country: "US", lat: 41.8781, lng: -87.6298 },
  { city: "Los Angeles", state: "CA", country: "US", lat: 34.0522, lng: -118.2437 },
  { city: "Dallas", state: "TX", country: "US", lat: 32.7767, lng: -96.7970 },
  { city: "Houston", state: "TX", country: "US", lat: 29.7604, lng: -95.3698 },
  { city: "Atlanta", state: "GA", country: "US", lat: 33.7490, lng: -84.3880 },
  { city: "New York", state: "NY", country: "US", lat: 40.7128, lng: -74.0060 },
  { city: "Miami", state: "FL", country: "US", lat: 25.7617, lng: -80.1918 },
  { city: "Seattle", state: "WA", country: "US", lat: 47.6062, lng: -122.3321 },
  { city: "Vancouver", state: "BC", country: "CA", lat: 49.2827, lng: -123.1207 },
  { city: "Calgary", state: "AB", country: "CA", lat: 51.0447, lng: -114.0719 },
];

export const cityLabel = (c: CityOption) => `${c.city}, ${c.state}`;

// Map US/CA state names to 2-letter codes (Photon returns full names).
const STATE_NAME_TO_CODE: Record<string, string> = {
  "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
  "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "Florida": "FL", "Georgia": "GA",
  "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL", "Indiana": "IN", "Iowa": "IA",
  "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
  "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS",
  "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV", "New Hampshire": "NH",
  "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY", "North Carolina": "NC",
  "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK", "Oregon": "OR", "Pennsylvania": "PA",
  "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD", "Tennessee": "TN",
  "Texas": "TX", "Utah": "UT", "Vermont": "VT", "Virginia": "VA", "Washington": "WA",
  "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY", "District of Columbia": "DC",
  "Alberta": "AB", "British Columbia": "BC", "Manitoba": "MB", "New Brunswick": "NB",
  "Newfoundland and Labrador": "NL", "Nova Scotia": "NS", "Northwest Territories": "NT",
  "Nunavut": "NU", "Ontario": "ON", "Prince Edward Island": "PE", "Quebec": "QC",
  "Québec": "QC", "Saskatchewan": "SK", "Yukon": "YT",
};

type PhotonFeature = {
  geometry: { coordinates: [number, number] };
  properties: {
    name?: string;
    city?: string;
    state?: string;
    countrycode?: string;
    osm_value?: string;
    postcode?: string;
  };
};

const US_ZIP = /^\d{5}$/;
const CA_POSTAL = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i;

export const isZipLike = (q: string) => {
  const v = q.trim().toUpperCase().replace(/\s+/g, "");
  return US_ZIP.test(v) || CA_POSTAL.test(v);
};

export const formatPostal = (raw: string, country: "US" | "CA" | undefined): string => {
  const v = raw.trim().toUpperCase();
  if (country === "CA" && /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(v.replace(/\s+/g, ""))) {
    const stripped = v.replace(/\s+/g, "");
    return `${stripped.slice(0, 3)} ${stripped.slice(3)}`;
  }
  return v;
};

export const validatePostal = (raw: string, country: "US" | "CA" | undefined): boolean => {
  if (!raw) return true; // optional
  const v = raw.trim().toUpperCase();
  if (country === "CA") return CA_POSTAL.test(v);
  return US_ZIP.test(v);
};

/**
 * Search cities/towns/villages via Photon (Komoot's free OSM-based geocoder).
 * No API key, CORS-enabled. Limited to US + Canada and place types
 * city / town / village / hamlet so we don't return random POIs.
 */
export const searchCitiesRemote = async (query: string, signal?: AbortSignal, limit = 8): Promise<CityOption[]> => {
  const q = query.trim();
  if (q.length < 2) return [];
  const isZip = isZipLike(q);
  // When the user types a ZIP, ask Photon for postcodes; otherwise ask for cities.
  const layer = isZip ? "postcode" : "city";
  const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=20&lang=en&layer=${layer}`;
  const res = await fetch(url, signal ? { signal } : {});
  if (!res.ok) throw new Error(`Photon ${res.status}`);
  const data = await res.json() as { features: PhotonFeature[] };

  const allowedTypes = new Set(["city", "town", "village", "hamlet", "postcode"]);
  const seen = new Set<string>();
  const out: CityOption[] = [];

  for (const f of data.features) {
    const p = f.properties;
    const cc = (p.countrycode || "").toUpperCase();
    if (cc !== "US" && cc !== "CA") continue;
    if (p.osm_value && !allowedTypes.has(p.osm_value)) continue;
    // For postcode results, prefer the linked city name; fall back to name (the ZIP itself).
    const name = p.city || p.name;
    if (!name) continue;
    // Skip postcode rows that didn't resolve to a city — they're not useful for freight.
    if (isZip && !p.city) continue;
    const stateCode = p.state ? (STATE_NAME_TO_CODE[p.state] || (p.state.length === 2 ? p.state.toUpperCase() : "")) : "";
    if (!stateCode) continue;
    const zip = p.postcode || (isZip ? p.name : undefined);
    const key = `${name}|${stateCode}|${cc}|${zip ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const [lng, lat] = f.geometry.coordinates;
    out.push({ city: name, state: stateCode, country: cc as "US" | "CA", lat, lng, ...(zip !== undefined ? { zip } : {}) });
    if (out.length >= limit) break;
  }
  return out;
};

// Distance between two coords (miles), via haversine.
export const distanceMiles = (a: CityOption, b: CityOption) => {
  const R = 3958.8;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(h)));
};